import React, { useState, useRef, useId } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { SiGithub, SiLinkedin } from "react-icons/si";
import {
  IconArrowRight,
  IconClock,
  IconMail,
  IconMapPin,
  IconMessageSquare,
  IconPhone,
  IconSparkles,
} from "../components/icons/ThinIcons";
import { useSiteConfig, useUiLabels } from "../context/LocaleContext";

const easeFluid = [0.32, 0.72, 0, 1] as const;

function ContactSection() {
  const config = useSiteConfig();
  const ui = useUiLabels();
  const formRef = useRef<HTMLFormElement>(null);
  const nameId = useId();
  const emailId = useId();
  const messageId = useId();
  const hintId = useId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const reduceMotion = useReducedMotion();
  const pageMotion = reduceMotion
    ? { initial: false, animate: { opacity: 1 } }
    : {
        initial: { opacity: 0, y: 16 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.75, ease: easeFluid },
      };

  const telHref = `tel:${config.phone.replace(/\s/g, "")}`;

  const sendEmail = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const name = formData.get("user_name") as string;
    const email = formData.get("user_email") as string;
    const message = formData.get("message") as string;

    if (!name.trim() || !email.trim() || !message.trim()) {
      setSubmitStatus({ success: false, message: ui.contact.validationRequired });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    emailjs
      .sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        formRef.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!
      )
      .then(
        () => {
          setSubmitStatus({
            success: true,
            message: ui.contact.successMessage,
          });
          formRef.current?.reset();
        },
        (error) => {
          setSubmitStatus({
            success: false,
            message: `${ui.contact.errorPrefix} ${error.text}`,
          });
        }
      )
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <motion.main {...pageMotion} className="flex w-full min-h-0 justify-center px-4 sm:px-8">
      <div className="py-24 sm:py-32 pb-28 w-full max-w-6xl">
        <header className="text-center max-w-3xl mx-auto mb-14 sm:mb-16">
          <p className="mb-4 inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-200/90 ring-1 ring-emerald-400/25">
            {ui.contact.badge}
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-heading leading-tight mb-5 text-balance-safe">
            {config.contactHeadline}
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 font-body leading-relaxed">{config.contactSubhead}</p>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 text-sm">
            <Link
              to="/about"
              className="inline-flex items-center gap-1.5 text-emerald-200/95 underline-offset-4 transition-colors duration-700 ease-fluid hover:text-white hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400 rounded-sm"
            >
              {ui.contact.linkAbout}
              <IconArrowRight className="w-4 h-4" aria-hidden />
            </Link>
            <span className="hidden sm:inline text-zinc-600" aria-hidden>
              ·
            </span>
            <Link
              to="/projects"
              className="inline-flex items-center gap-1.5 text-emerald-200/95 underline-offset-4 transition-colors duration-700 ease-fluid hover:text-white hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400 rounded-sm"
            >
              {ui.contact.linkProjects}
              <IconArrowRight className="w-4 h-4" aria-hidden />
            </Link>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-5 space-y-6">
            <section aria-labelledby="contact-why-title">
              <h2 id="contact-why-title" className="text-lg font-heading font-bold text-white mb-5">
                {ui.contact.whyHeading}
              </h2>
              <ul className="space-y-4">
                {config.contactHighlights.map((item) => (
                  <li key={item.title} className="bezel-outer">
                    <div className="bezel-inner p-5">
                      <div className="flex gap-3">
                        <IconSparkles className="w-5 h-5 text-emerald-300/90 shrink-0 mt-0.5" aria-hidden />
                        <div>
                          <h3 className="text-sm font-heading font-bold text-white mb-1.5">{item.title}</h3>
                          <p className="text-sm font-body text-zinc-400 leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <div className="bezel-outer">
              <div className="bezel-inner bg-gradient-to-br from-violet-500/[0.07] to-transparent p-5 sm:p-6">
                <h2 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-200/85 mb-5">{ui.contact.directChannels}</h2>
                <ul className="space-y-2">
                  <li>
                    <a
                      href={`mailto:${config.email}`}
                      className="flex items-start gap-3 rounded-2xl p-3 -m-1 transition-[background-color] duration-700 ease-fluid hover:bg-white/[0.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400 active:scale-[0.99]"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] ring-1 ring-white/[0.08] text-emerald-200">
                        <IconMail className="w-5 h-5" aria-hidden />
                      </span>
                      <span>
                        <span className="block text-xs text-zinc-500">{ui.contact.emailLabel}</span>
                        <span className="text-sm font-medium text-white break-all">{config.email}</span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a
                      href={telHref}
                      className="flex items-start gap-3 rounded-2xl p-3 -m-1 transition-[background-color] duration-700 ease-fluid hover:bg-white/[0.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400 active:scale-[0.99]"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] ring-1 ring-white/[0.08] text-emerald-200">
                        <IconPhone className="w-5 h-5" aria-hidden />
                      </span>
                      <span>
                        <span className="block text-xs text-zinc-500">{ui.contact.phoneLabel}</span>
                        <span className="text-sm font-medium text-white">{config.phone}</span>
                      </span>
                    </a>
                  </li>
                  <li className="flex items-start gap-3 rounded-2xl p-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] ring-1 ring-white/[0.08] text-emerald-200">
                      <IconMapPin className="w-5 h-5" aria-hidden />
                    </span>
                    <span>
                      <span className="block text-xs text-zinc-500">{ui.contact.locationLabel}</span>
                      <span className="text-sm font-medium text-white">{config.locationDisplay}</span>
                    </span>
                  </li>
                </ul>

                <p className="mt-5 flex items-center gap-2 text-xs text-zinc-500 font-body">
                  <IconClock className="w-3.5 h-3.5 shrink-0 text-emerald-200/80" aria-hidden />
                  {config.contactResponsePledge}
                </p>

                <div className="mt-6 pt-5 border-t border-white/[0.08]">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">{ui.contact.socialHeading}</p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={config.social.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-white/[0.1] bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-white transition-[transform,background-color] duration-700 ease-fluid hover:bg-white/[0.08] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400 active:scale-[0.98] min-h-[44px]"
                    >
                      <SiLinkedin className="w-4 h-4" aria-hidden />
                      LinkedIn
                    </a>
                    <a
                      href={config.social.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-white/[0.1] bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-white transition-[transform,background-color] duration-700 ease-fluid hover:bg-white/[0.08] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400 active:scale-[0.98] min-h-[44px]"
                    >
                      <SiGithub className="w-4 h-4" aria-hidden />
                      GitHub
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <section className="bezel-outer" aria-labelledby="contact-form-title">
              <div className="bezel-inner p-6 sm:p-9">
                <div className="flex items-start gap-3 mb-7">
                  <IconMessageSquare className="w-6 h-6 text-emerald-300 shrink-0 mt-1" aria-hidden />
                  <div>
                    <h2 id="contact-form-title" className="text-xl sm:text-2xl font-heading font-bold text-white">
                      {ui.contact.formTitle}
                    </h2>
                    <p id={hintId} className="mt-2 text-sm text-zinc-400 font-body leading-relaxed">
                      {config.contactFormHint}
                    </p>
                  </div>
                </div>

                <form ref={formRef} onSubmit={sendEmail} noValidate className="space-y-5" aria-describedby={hintId}>
                  <div>
                    <label htmlFor={nameId} className="block text-sm font-medium text-zinc-300 mb-1.5">
                      {ui.contact.nameLabel}
                    </label>
                    <input
                      id={nameId}
                      type="text"
                      name="user_name"
                      autoComplete="name"
                      required
                      placeholder={ui.contact.namePlaceholder}
                      className="w-full min-h-[48px] rounded-2xl border border-white/[0.1] bg-white/[0.05] px-4 py-3 text-base text-white placeholder-zinc-600 transition-[box-shadow] duration-700 ease-fluid focus:border-emerald-400/40 focus:outline-none focus:ring-2 focus:ring-emerald-400/25"
                    />
                  </div>

                  <div>
                    <label htmlFor={emailId} className="block text-sm font-medium text-zinc-300 mb-1.5">
                      {ui.contact.emailLabel}
                    </label>
                    <input
                      id={emailId}
                      type="email"
                      name="user_email"
                      autoComplete="email"
                      required
                      placeholder={ui.contact.emailPlaceholder}
                      className="w-full min-h-[48px] rounded-2xl border border-white/[0.1] bg-white/[0.05] px-4 py-3 text-base text-white placeholder-zinc-600 transition-[box-shadow] duration-700 ease-fluid focus:border-emerald-400/40 focus:outline-none focus:ring-2 focus:ring-emerald-400/25"
                    />
                  </div>

                  <div>
                    <label htmlFor={messageId} className="block text-sm font-medium text-zinc-300 mb-1.5">
                      {ui.contact.messageLabel}
                    </label>
                    <textarea
                      id={messageId}
                      name="message"
                      required
                      rows={5}
                      placeholder={ui.contact.messagePlaceholder}
                      className="w-full resize-y min-h-[140px] rounded-2xl border border-white/[0.1] bg-white/[0.05] px-4 py-3 text-base text-white placeholder-zinc-600 transition-[box-shadow] duration-700 ease-fluid focus:border-emerald-400/40 focus:outline-none focus:ring-2 focus:ring-emerald-400/25"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group w-full inline-flex items-center justify-center gap-0 rounded-full bg-gradient-to-r from-emerald-400/90 to-teal-500/90 px-6 py-3.5 text-sm font-semibold text-[#050505] transition-[transform,opacity] duration-700 ease-fluid hover:brightness-[1.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] min-h-[48px]"
                  >
                    {isSubmitting ? (
                      ui.contact.submitting
                    ) : (
                      <>
                        <span>{ui.contact.submit}</span>
                        <span className="ml-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/10 transition-[transform] duration-700 ease-fluid group-hover:translate-x-0.5">
                          <IconArrowRight className="w-4 h-4" aria-hidden />
                        </span>
                      </>
                    )}
                  </button>

                  {submitStatus && (
                    <div
                      role={submitStatus.success ? "status" : "alert"}
                      aria-live={submitStatus.success ? "polite" : "assertive"}
                      className={`rounded-2xl p-4 text-center text-sm font-medium ring-1 ${
                        submitStatus.success
                          ? "bg-emerald-500/15 text-emerald-100 ring-emerald-500/25"
                          : "bg-red-500/15 text-red-100 ring-red-500/25"
                      }`}
                    >
                      {submitStatus.message}
                    </div>
                  )}
                </form>
              </div>
            </section>
          </div>
        </div>
      </div>
    </motion.main>
  );
}

export default ContactSection;
